import { useState } from 'react'
import { Link, redirect, useNavigate } from 'react-router'
import { createClient } from '@/api-client'
import { Me } from '@/api-client/Me'
import { Subscriptions } from '@/api-client/Subscriptions'
import { BranchPageHeading } from '~/components/subscription/branch-page-heading'
import logo from './logo.svg'

export const loader = async () => {
  return await createClient(Me)
    .getMe()
    .then(() => redirect('/subscription/branch/mypage'))
    .catch(() => {}) // アカウント情報が取れなかったらログイン画面を表示
}

export const SubscriptionBranchLogin: React.FC = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const inputClass = 'w-full border border-gray rounded-lg px-3 py-3 text-[14px] outline-none focus:border-black transition-colors bg-white'

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault()

    try {
      await createClient(Subscriptions).postSubscriptionsSessions({
        email,
        password,
        signup: '0',
      })

      navigate('/subscription/branch/mypage')
    } catch {
      setMessage('メールアドレスまたはパスワードが違います。')
    }
  }

  return (
    <div className="min-h-screen bg-bg font-sans text-main">
      <header className="bg-white border-b border-gray/40">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center">
          <Link to="/subscription/branch">
            <img src={logo} alt="FLOWER" className="h-7" />
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-12">
        <BranchPageHeading main="ログイン" className="mb-8" />

        {message && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-2">
            <svg className="w-4 h-4 mt-0.5 shrink-0 text-red-500" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.3" />
              <path d="M8 5v3.5M8 11h.01" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <p className="text-[13px] text-red-600 flex-1">{message}</p>
            <button onClick={() => setMessage('')} className="text-red-400 hover:text-red-600 transition-colors text-[14px] leading-none">
              ×
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <section className="bg-white rounded-2xl p-6 mb-4">
            <div className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-[12px] text-dark-gray mb-2">
                  メールアドレス <span className="text-accent">*</span>
                </label>
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="example@email.com"
                  className={inputClass}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-[12px] text-dark-gray mb-2">
                  パスワード <span className="text-accent">*</span>
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="パスワード"
                    className={inputClass + ' pr-10'}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-gray hover:text-black transition-colors"
                    aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M2 2l12 12M6.5 6.55A2 2 0 0 0 9.45 9.5M4.19 4.22C2.82 5.13 1.73 6.46 1 8c1.33 2.93 4 5 7 5a7.2 7.2 0 0 0 3.81-1.08M6.8 3.1A7.4 7.4 0 0 1 8 3c3 0 5.67 2.07 7 5a8.5 8.5 0 0 1-1.72 2.56"
                          stroke="currentColor"
                          strokeWidth="1.2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <ellipse cx="8" cy="8" rx="7" ry="4.5" stroke="currentColor" strokeWidth="1.2" />
                        <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.2" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </section>

          <p className="text-right text-[12px] text-dark-gray mb-6">
            <a href="/password_reset/new" className="underline hover:text-black transition-colors">
              パスワードをお忘れの方
            </a>
          </p>

          <button type="submit" className="w-full h-14 bg-black text-white text-[15px] font-medium rounded-xl hover:bg-black/80 transition-colors">
            ログイン
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-[13px] text-dark-gray">アカウントをお持ちでない方はこちら</p>
          <Link
            to="/subscription/branch/new"
            className="inline-block mt-2 text-[13px] font-medium text-black underline decoration-solid hover:opacity-70 transition-opacity"
          >
            新規登録
          </Link>
        </div>
      </main>
    </div>
  )
}
